import {
  useQuery as useVueQuery,
  useMutation as useVueMutation,
  type QueryClient,
  useQueryClient as useVueQueryClient,
  type UseQueryOptions,
  type QueryKey,
} from '@tanstack/vue-query'

import { type VueMutationObserverOptions } from '@tanstack/vue-query/build/lib/useMutation'
import { type MaybeRef } from '@tanstack/vue-query/build/lib/types'
import { useContext } from './context'

/**
 * Get the QueryClient for Vue Query, handling both SSR and client-side contexts.
 * This composable should be used instead of directly calling useQueryClient()
 * to ensure proper SSR compatibility. Otherwise an error will occur during SSR,
 * because useQueryClient() relies on Vue's provide/inject mechanism, which
 * is not used during SSR in our vue-query plugin.
 *
 * @returns QueryClient instance from SSR context or Vue context (client-side)
 */
export function useQueryClient(): QueryClient {
  const { ssrContext } = useContext()

  // Get QueryClient either from SSR context, or Vue context
  const queryClient: QueryClient =
    (ssrContext != null && (ssrContext as any).VueQuery) || useVueQueryClient()

  return queryClient
}

/**
 * Composable for fetching data with Vue Query. It uses a SSR-friendly QueryClient.
 * This composable should be used instead of directly calling useQuery() from
 * '@tanstack/vue-query'.
 *
 * @param options Options for the query
 */
export function useQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: MaybeRef<
    Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryClient'>
  >,
) {
  const queryClient = useQueryClient()

  return useVueQuery({ ...options, queryClient })
}

export function useQueryWithInitialData<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: MaybeRef<
    Omit<
      UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
      'initialData' | 'queryClient'
    > & {
      initialData: TQueryFnData | (() => TQueryFnData)
    }
  >,
) {
  const queryClient = useQueryClient()
  // Needed otherwise, Typescript will infer the wrong type for the options, and
  // the data from the query may be undefined, which is incorrect.
  const newOptions: MaybeRef<
    Omit<
      UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
      'initialData'
    > & {
      initialData: TQueryFnData | (() => TQueryFnData)
    }
  > = { ...options, queryClient }

  return useVueQuery(newOptions)
}

/**
 * Composable for mutations with Vue Query. It uses a SSR-friendly QueryClient.
 * This composable should be used instead of directly calling useMutation() from
 * '@tanstack/vue-query'.
 *
 * @param mutationOptions Options for the mutation
 */
export function useMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  options: MaybeRef<
    Omit<
      VueMutationObserverOptions<TData, TError, TVariables, TContext>,
      'queryClient'
    >
  >,
) {
  const queryClient = useQueryClient()

  return useVueMutation({ ...options, queryClient })
}
